import { buildBucketKeys } from "./adStatsTime";

export const CHART_COLORS = {
  impressions: "#2a78d6",
  clicks: "#eb6834",
  rewards: "#1baf7a",
  verified: "#008300",
  dismissals: "#eda100",
  errors: "#e34948",
};

export const CHART_VIEWS = [
  { key: "delivery", label: "Impressions & clicks" },
  { key: "ctr", label: "Click-through rate" },
  { key: "rewards", label: "Rewards" },
  { key: "issues", label: "Dismissals & errors" },
];

const EMPTY_COUNTS = {
  impressions: 0,
  clicks: 0,
  rewardsEarned: 0,
  verifiedRewards: 0,
  dismissals: 0,
  errors: 0,
  clickThroughRate: 0,
};

const MAX_X_AXIS_LABELS = 10;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AXIS_LABEL_STYLE = { colors: "#8898aa", fontSize: "11px" };
const AXIS_TITLE_STYLE = { color: "#8898aa", fontSize: "11px", fontWeight: 500 };

export const buildTimelinePoints = ({ timeline, range }) => {
  const byKey = new Map(
    (timeline || []).map((bucket) => [bucket.bucket, bucket]),
  );
  const generatedKeys = buildBucketKeys({
    from: new Date(range.from),
    to: new Date(Math.min(new Date(range.to).getTime(), Date.now())),
    granularity: range.granularity,
    timeZone: range.timezone,
  });
  const keys = [...new Set([...generatedKeys, ...byKey.keys()])].sort();

  return keys.map((key) => ({
    key,
    counts: byKey.get(key) || EMPTY_COUNTS,
  }));
};

const parseBucketKey = (key) => {
  const [day, hour] = key.split("T");
  const [year, month, date] = day.split("-").map(Number);
  return {
    year,
    month,
    date,
    hour: hour === undefined ? null : Number(hour),
  };
};

const hourLabel = (hour) => `${String(hour).padStart(2, "0")}:00`;

const axisLabel = (key, spansYears) => {
  const { year, month, date, hour } = parseBucketKey(key);
  if (hour !== null && hour !== 0) return hourLabel(hour);
  const yearSuffix = spansYears ? ` '${String(year).slice(2)}` : "";
  return `${date} ${MONTHS[month - 1]}${yearSuffix}`;
};

const tooltipLabel = (key) => {
  const { year, month, date, hour } = parseBucketKey(key);
  const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, date)).getUTCDay()];
  const day = `${weekday}, ${date} ${MONTHS[month - 1]} ${year}`;
  return hour === null ? day : `${day} · ${hourLabel(hour)}`;
};

const seriesData = (points, pick) => points.map((point) => pick(point.counts));

const valuesOf = (series) => series.data;

const countAxis = (series, extra = {}) => {
  const max = Math.max(0, ...valuesOf(series));
  const isSmall = max <= 5;
  return {
    min: 0,
    tickAmount: isSmall ? Math.max(max, 1) : 5,
    ...(isSmall ? { max: Math.max(max, 1) } : {}),
    decimalsInFloat: 0,
    labels: {
      style: AXIS_LABEL_STYLE,
      formatter: (value) => Math.round(value).toLocaleString(),
    },
    ...extra,
  };
};

const axisTitle = (text) => ({ text, style: AXIS_TITLE_STYLE });

const buildDelivery = (points) => {
  const impressions = {
    name: "Impressions",
    type: "column",
    data: seriesData(points, (counts) => counts.impressions),
  };
  const clicks = {
    name: "Clicks",
    type: "line",
    data: seriesData(points, (counts) => counts.clicks),
  };
  return {
    chartType: "line",
    series: [impressions, clicks],
    colors: [CHART_COLORS.impressions, CHART_COLORS.clicks],
    stroke: { width: [0, 3], curve: "straight" },
    yaxis: [
      countAxis(impressions, {
        seriesName: "Impressions",
        title: axisTitle("Impressions"),
      }),
      countAxis(clicks, {
        seriesName: "Clicks",
        opposite: true,
        title: axisTitle("Clicks"),
      }),
    ],
  };
};

const buildCtr = (points) => {
  const ctr = {
    name: "CTR (%)",
    data: seriesData(points, (counts) =>
      Number((counts.clickThroughRate * 100).toFixed(2)),
    ),
  };
  return {
    chartType: "area",
    series: [ctr],
    colors: [CHART_COLORS.clicks],
    stroke: { width: 2, curve: "straight" },
    yaxis: [
      {
        min: 0,
        decimalsInFloat: 2,
        labels: {
          style: AXIS_LABEL_STYLE,
          formatter: (value) => `${Number(value).toFixed(2)}%`,
        },
      },
    ],
    tooltipValueFormatter: (value) => `${Number(value).toFixed(2)}%`,
  };
};

const buildGroupedColumns = (points, definitions) => {
  const series = definitions.map(({ name, pick }) => ({
    name,
    data: seriesData(points, pick),
  }));
  const largest = series.reduce(
    (best, candidate) =>
      Math.max(...valuesOf(candidate)) > Math.max(...valuesOf(best))
        ? candidate
        : best,
    series[0],
  );
  return {
    chartType: "bar",
    series,
    colors: definitions.map(({ color }) => color),
    stroke: { width: 0 },
    yaxis: [countAxis(largest)],
  };
};

const VIEW_BUILDERS = {
  delivery: buildDelivery,
  ctr: buildCtr,
  rewards: (points) =>
    buildGroupedColumns(points, [
      {
        name: "Rewards earned",
        pick: (counts) => counts.rewardsEarned,
        color: CHART_COLORS.rewards,
      },
      {
        name: "Verified",
        pick: (counts) => counts.verifiedRewards,
        color: CHART_COLORS.verified,
      },
    ]),
  issues: (points) =>
    buildGroupedColumns(points, [
      {
        name: "Dismissals",
        pick: (counts) => counts.dismissals,
        color: CHART_COLORS.dismissals,
      },
      {
        name: "Errors",
        pick: (counts) => counts.errors,
        color: CHART_COLORS.errors,
      },
    ]),
};

export const buildTimelineChart = (viewKey, points) => {
  const view = (VIEW_BUILDERS[viewKey] || VIEW_BUILDERS.delivery)(points);
  const spansYears =
    points.length > 0 &&
    parseBucketKey(points[0].key).year !==
      parseBucketKey(points[points.length - 1].key).year;
  const categories = points.map((point) => axisLabel(point.key, spansYears));
  const tooltipLabels = points.map((point) => tooltipLabel(point.key));

  const options = {
    chart: {
      type: view.chartType,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      fontFamily: "inherit",
    },
    colors: view.colors,
    stroke: view.stroke,
    fill: {
      type: view.chartType === "area" ? "gradient" : "solid",
      ...(view.chartType === "area"
        ? {
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.35,
              opacityTo: 0.04,
              stops: [0, 100],
            },
          }
        : {}),
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        borderRadiusApplication: "end",
        columnWidth: "62%",
      },
    },
    markers: { size: 0, hover: { size: 4 } },
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { size: 6 },
    },
    grid: {
      borderColor: "#e9ecef",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      type: "category",
      categories,
      tickAmount: Math.min(categories.length, MAX_X_AXIS_LABELS),
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        style: AXIS_LABEL_STYLE,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: view.yaxis,
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (value, { dataPointIndex }) =>
          tooltipLabels[dataPointIndex] ?? value,
      },
      ...(view.tooltipValueFormatter
        ? { y: { formatter: view.tooltipValueFormatter } }
        : {}),
    },
  };

  return {
    series: view.series,
    options,
    hasData: view.series.some((series) =>
      series.data.some((value) => value > 0),
    ),
  };
};
